/* eslint-disable one-var, react/sort-comp, react/jsx-first-prop-new-line, react/jsx-closing-bracket-location */
import React, { Component, PropTypes } from 'react';
import Keys from 'es6/core/keys';
import classnames from 'classnames';
import { css } from 'aphrodite';
import { createAndInjectStyleSheet } from 'es6/helpers/css';
import { t } from 'es6/services/localization';
import { NavButton, Navigation } from 'es6/components/apps/navigation';
import Glyph, { CORRECT_RESULT, REMOVE, REVIEW } from 'es6/components/common/glyph';
import SoundPlayer, { SOUND_PLAYER_UNSTYLED } from 'es6/components/common/sound-player';
import SoundManager from 'es6/services/sound-manager';
import FitText from 'es6/components/common/fit-text';
import MouseoverDictionary from 'iknow-mouseover-dictionary/react';
import BasicModal from 'es6/components/common/modals/basic';
import cookies from 'es6/services/cookies';
import {
  ANSWER_CONFIRMATION_ANSWER_TEXT_COLOR,
  ANSWER_CONFIRMATION_ANSWER_BACKGROUND_COLOR,
} from 'es6/core/styles/colors';
import AlignBottom from 'es6/components/layout/align-bottom';
import FadeInSentenceTask, {
  ANIMATION_START,
  ANIMATION_FADE_IN_SENTENCE,
  ANIMATION_FADE_IN_SENTENCE_END,
  ANIMATION_PLAY_SENTENCE,
  ANIMATION_FADE_IN_SENTENCE_TRANSLATION,
  ANIMATION_FADE_IN_SENTENCE_TRANSLATION_END,
  ANIMATION_END,
} from 'es6/components/apps/screens/answer-confirmation-tasks';
import FillInTheBlankAnswer from 'es6/components/apps/fill-in-the-blank-answer';
import { parseIMEHtml } from 'es6/components/apps/ime';

const STUDY_LESS_MORE_MODAL_SKIP_COOKIE = 'skip_study_more_less';

export const
  STUDY_LESS = 0,
  STUDY_MORE = 1;

export const
  MULTIPLE_CHOICE_ANSWER   = 0,
  FILL_IN_THE_BLANK_ANSWER = 1;

export default class AnswerConfirmationScreen extends Component {
  constructor() {
    super();

    this.state = {
      animationState: ANIMATION_START,
      isStudyLessMoreModalOpen: false,
    };

    this.setAnimationState = animationState => this.setState({ animationState });

    // Event callbacks.
    this.onSentenceTransitionEnd = e => {
      if (this.state.animationState === ANIMATION_FADE_IN_SENTENCE) {
        this.setAnimationState(ANIMATION_FADE_IN_SENTENCE_END);
      }
    };
    this.onSentenceTranslationTransitionEnd = e => {
      if (this.state.animationState === ANIMATION_FADE_IN_SENTENCE_TRANSLATION) {
        this.setAnimationState(ANIMATION_FADE_IN_SENTENCE_TRANSLATION_END);
      }

      // Prevent the sentence translation transitionend event from bubbling up and triggering the
      // transitionend event on the sentence container.
      e.stopPropagation();
    };

    this.playSentenceIfNeeded = () => {
      // If the sound hasn't been played yet, play it.
      if (this.state.animationState < ANIMATION_PLAY_SENTENCE) {
        this.playSentence();
      }
    };

    this.onNext = () => {
      // Skip to end of animation when user presses the next button.
      if (this.state.animationState !== ANIMATION_END) {
        this.playSentenceIfNeeded();

        this.setAnimationState(ANIMATION_END);
      } else {
        // If the animation is already over, show the next screen.
        this.props.onNext();
      }
    };

    this.onKeyDown = e => {
      // If the user presses enter or space, skip to the end of the animation.
      if (e.keyCode === Keys.ENTER && this.state.animationState !== ANIMATION_END) {
        // Prevent nav buttons from being pressed if one currently has focus.
        e.preventDefault();

        this.playSentenceIfNeeded();

        this.setAnimationState(ANIMATION_END);
      } else if (e.keyCode === Keys.SPACE && this.props.sentence && this.props.sentence.soundUrl) {
        // Also skip the animation sequence when the user presses space, but always replay the
        // sentence audio.
        this.playSentence();

        this.setAnimationState(ANIMATION_END);
      }
    };

    this.openStudyLessModal = () => this.openStudyLessMoreModal(STUDY_LESS);
    this.openStudyMoreModal = () => this.openStudyLessMoreModal(STUDY_MORE);
    this.closeStudyLessMoreModal = () => {
      this.setState({
        isStudyLessMoreModalOpen: false,
        onStudyLessMoreModalConfirm: null,
      });
    };
  }

  playSentence() {
    if (this.props.sentence && this.props.sentence.soundUrl) {
      SoundManager.play(this.props.sentence.soundUrl);
    }
  }

  openStudyLessMoreModal(lessOrMore) {
    const skipModal = cookies.get(STUDY_LESS_MORE_MODAL_SKIP_COOKIE);

    if (skipModal) {
      // Just call the callback if the skip cookie is set.
      this.props.onStudyLessMore(lessOrMore);
    } else {
      this.setState({
        isStudyLessMoreModalOpen: true,
        onStudyLessMoreModalConfirm: () => {
          this.props.onStudyLessMore(lessOrMore);

          // If the user clicks the confirm button, skip the dialog the next time they press one of
          // the study less/more buttons.
          cookies.set(STUDY_LESS_MORE_MODAL_SKIP_COOKIE, true, { expires: 356, path: '/' });

          this.closeStudyLessMoreModal();
        },
      });
    }
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);

    // Stop playing the sentence if it is currently playing.
    if (this.props.sentence && this.props.sentence.soundUrl) {
      SoundManager.stop(this.props.sentence.soundUrl);
    }
  }

  render() {
    const { answerType, answerText, answerLanguage, imeHtml, sentence } = this.props;

    const animationTaskProps = {
      setAnimationState: this.setAnimationState,
      animationState: this.state.animationState,
      sentence,
    };

    const answerClassName = classnames(css(styles.answer), {
      [css(styles.answerMultipleChoice)]: answerType === MULTIPLE_CHOICE_ANSWER,
      [css(styles.answerFillInTheBlank)]: answerType === FILL_IN_THE_BLANK_ANSWER,
    });

    const answerContentClassName = classnames({
      [css(styles.answerMultipleChoiceContent)]: answerType === MULTIPLE_CHOICE_ANSWER,
      [css(styles.answerFillInTheBlankContent)]: answerType === FILL_IN_THE_BLANK_ANSWER,
    });

    const answerCorrectIconClassName = classnames(css(styles.answerCorrectIcon), {
      [css(styles.answerMultipleChoiceCorrectIcon)]: answerType === MULTIPLE_CHOICE_ANSWER,
      [css(styles.answerFillInTheBlankCorrectIcon)]: answerType === FILL_IN_THE_BLANK_ANSWER,
    });

    // Need to use non-Aphrodite CSS to style <b> elements inside the sentence data.
    // TODO: Find out if there is any to style tags with Aphrodite.
    const sentenceContainerClassName = classnames('sentence', css(styles.sentenceContainer), {
      [css(styles.hidden)]: this.state.animationState < ANIMATION_FADE_IN_SENTENCE,
      [css(styles.fadeIn)]: this.state.animationState === ANIMATION_FADE_IN_SENTENCE,
    });

    const sentenceClassName = classnames(css(styles.sentence), {
      [css(styles.sentenceWithImage)]: sentence && sentence.imageUrl,
    });

    const sentenceTranslationClassName = classnames(css(styles.sentenceTranslation), {
      [css(styles.hidden)]: this.state.animationState < ANIMATION_FADE_IN_SENTENCE_TRANSLATION,
      [css(styles.fadeIn)]: this.state.animationState === ANIMATION_FADE_IN_SENTENCE_TRANSLATION,
    });

    return (
      <div className="screen">
        {this.state.animationState < ANIMATION_END && <FadeInSentenceTask {...animationTaskProps} />}

        <div className={classnames('screen-content', css(styles.screen))}>
          <div className={css(styles.message)}>
            {t('js_iknow.well done!')}
          </div>

          <div className={answerClassName}>
            <Glyph type={CORRECT_RESULT} className={answerCorrectIconClassName} />

            <span className={answerContentClassName}>
              <FitText key={answerText}>
                <span className="forceLanguage" lang={answerLanguage}>
                  <MouseoverDictionary>
                    {answerType === FILL_IN_THE_BLANK_ANSWER ?
                      <FillInTheBlankAnswer imeState={parseIMEHtml(imeHtml)} /> :
                      answerText}
                  </MouseoverDictionary>
                </span>
              </FitText>
            </span>
          </div>

          {sentence && (
            <div className={sentenceContainerClassName} onTransitionEnd={this.onSentenceTransitionEnd}>
              {sentence.imageUrl && (
                <div style={{ backgroundImage: `url(${sentence.imageUrl})` }} className={css(styles.sentenceImage)} />
              )}

              <div className={sentenceClassName}>
                <div className={css(styles.sentenceContent)}>
                  <AlignBottom>
                    {sentence.soundUrl &&
                      <SoundPlayer source={sentence.soundUrl} variant={SOUND_PLAYER_UNSTYLED} className={css(styles.sentenceSoundPlayer)} />}

                    <FitText key={sentence.text}>
                      <div className={css(styles.sentenceText)}>
                        <span className="forceLanguage" lang={sentence.language}>
                          <MouseoverDictionary html={sentence.text} />
                        </span>
                      </div>
                      {sentence.transliteration &&
                        <div className={css(styles.sentenceTransliteration)}
                          dangerouslySetInnerHTML={{ __html: sentence.transliteration }} />}
                    </FitText>
                  </AlignBottom>
                </div>

                {sentence.translation && (
                  <div className={sentenceTranslationClassName} onTransitionEnd={this.onSentenceTranslationTransitionEnd}>
                    <FitText><span dangerouslySetInnerHTML={{ __html: sentence.translation }} /></FitText>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={css(styles.bottomMessage)} dangerouslySetInnerHTML={{ __html: t('choose-item-difficulty') }} />
        </div>

        <Navigation>
          <NavButton align="left" onClick={this.props.onRemove} title={t('js_iknow.remove')} className="btn-danger btn-nav-icon">
            <Glyph type={REMOVE} />
          </NavButton>
          <NavButton align="left" onClick={this.props.onReview} title={t('js_iknow.review')} className="btn-nav-icon">
            <Glyph type={REVIEW} />
          </NavButton>
          <NavButton align="left" className="btn-nav-icon" onClick={this.props.onIncorrect}>
            {t('mark-as-incorrect')}
          </NavButton>

          <NavButton align="center" className={css(styles.navStudyLessMore)} onClick={this.openStudyLessModal}>{t('easy')}</NavButton>
          <NavButton align="center" className={css(styles.navStudyLessMore)} onClick={this.openStudyMoreModal}>{t('hard')}</NavButton>

          <NavButton align="right" focused onClick={this.onNext}>{t('js_iknow.next')}</NavButton>
        </Navigation>

        <BasicModal
          isOpen={this.state.isStudyLessMoreModalOpen}
          onClose={this.closeStudyLessMoreModal}
          title={t('study-more-less-dialogue-header')}
          className={css(styles.modal)}
        >
          <div className={css(styles.modalContent)}>
            <p dangerouslySetInnerHTML={{ __html: t('study-more-less-dialogue-description') }} />
          </div>

          <footer className={css(styles.modalButtonContainer)}>
            <button onClick={this.state.onStudyLessMoreModalConfirm} className="btn btn-bar btn-primary ok">{t('ok')}</button>
          </footer>
        </BasicModal>
      </div>
    );
  }
}

AnswerConfirmationScreen.propTypes = {
  onRemove:         PropTypes.func.isRequired,
  onIncorrect:      PropTypes.func.isRequired,
  onReview:         PropTypes.func.isRequired,
  onNext:           PropTypes.func.isRequired,
  onStudyLessMore:  PropTypes.func.isRequired,
  answerType:       PropTypes.number,
  answerText:       PropTypes.string,
  answerLanguage:   PropTypes.string,
  imeHtml:          PropTypes.string,
  sentence:         PropTypes.shape({
    text:            PropTypes.string,
    transliteration: PropTypes.string,
    translation:     PropTypes.string,
    language:        PropTypes.string,
    imageUrl:        PropTypes.string,
    soundUrl:        PropTypes.string,
  }),
};

const ANSWER_HEIGHT = 60;
const ANSWER_HEIGHT_PX = `${ANSWER_HEIGHT}px`;

const styles = createAndInjectStyleSheet({
  screen: {
    lineHeight: '1.2',
  },
  message: {
    position: 'absolute',
    top: '20px',
    width: '100%',
    textAlign: 'center',
    fontSize: '14px',
  },
  answer: {
    position: 'absolute',
    top: '70px',
    left: '100px',
    right: '100px',
  },
  answerMultipleChoice: {
    color: ANSWER_CONFIRMATION_ANSWER_TEXT_COLOR,
    backgroundColor: ANSWER_CONFIRMATION_ANSWER_BACKGROUND_COLOR,
    borderRadius: '5px',
    padding: `10px ${15 + ANSWER_HEIGHT}px 10px 15px`,
    height: ANSWER_HEIGHT_PX,
    maxHeight: ANSWER_HEIGHT_PX,
    fontSize: '24px',
  },
  answerFillInTheBlank: {
    fontSize: '28px',
    textAlign: 'center',
    height: '90px',
  },
  answerMultipleChoiceContent: {
  },
  answerFillInTheBlankContent: {
    display: 'inline-block',
    verticalAlign: 'middle',
    maxHeight: '90px',
  },
  answerCorrectIcon: {
    width: ANSWER_HEIGHT_PX,
    height: ANSWER_HEIGHT_PX,
    fontSize: ANSWER_HEIGHT_PX,
    lineHeight: '1',
  },
  answerMultipleChoiceCorrectIcon: {
    position: 'absolute',
    right: '10px',
    top: '10px',
  },
  answerFillInTheBlankCorrectIcon: {
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: '-80px',
    marginRight: '15px',
  },
  sentenceContainer: {
    position: 'absolute',
    top: '170px',
    left: '100px',
    right: '100px',
  },
  sentence: {
  },
  sentenceWithImage: {
    marginRight: '210px',
  },
  sentenceImage: {
    float: 'right',
    marginLeft: '10px',
    marginTop: '10px',
    width: '180px',
    height: '166px',
    borderRadius: '8px',
    backgroundPosition: 'center',
    backgroundSize: '180px 168px',
  },
  sentenceContent: {
    height: '80px',
    maxHeight: '80px',
  },
  sentenceSoundPlayer: {
    position: 'absolute',
    right: '100%',
    paddingRight: '5px',
    lineHeight: '31px',
    fontSize: '20px',
  },
  sentenceText: {
    fontSize: '26px',
    verticalAlign: 'middle',
  },
  sentenceTransliteration: {
    marginTop: '5px',
    fontSize: '14px',
  },
  sentenceTranslation: {
    marginTop: '15px',
    borderTop: '1px solid rgba(255, 255, 255, 0.3)',
    paddingTop: '15px',
    fontSize: '22px',
    maxHeight: '80px',
  },
  hidden: {
    opacity: '0',
  },
  fadeIn: {
    transition: 'opacity 500ms',
  },
  navStudyLessMore: {
    // Override nav button default min-width for study less/more buttons.
    minWidth: '80px !important',
  },
  modal: {
    maxWidth: '500px',
  },
  modalContent: {
    padding: '15px',
  },
  modalButtonContainer: {
    textAlign: 'center',
    padding: '10px 0',
  },
  bottomMessage: {
    position: 'absolute',
    bottom: '5px',
    width: '100%',
    textAlign: 'center',
    fontSize: '12px',
  },
});
